import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../../../components/Header';
import talent14Logo from '../../../../assets/TALENT14_LOGO.png';

const HeroSection = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const features = [
    {
      id: 0,
      title: 'Kademeli Öğrenme',
      subtitle: 'Path Sistemi',
      description: 'Her öğrenci kendi hızında ilerler, kendi yolunu çizer.',
      color: 'from-blue-500 via-cyan-500 to-teal-500',
      icon: '🎯',
      visual: (
        <div className="relative w-full h-full flex items-center justify-center p-4">
          {/* Roadmap Visualization */}
          <div className="relative w-full h-full flex flex-col justify-center">
            {/* Path Line */}
            <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 0 }}>
              <defs>
                <linearGradient id="roadmapGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#10b981" />
                  <stop offset="25%" stopColor="#3b82f6" />
                  <stop offset="50%" stopColor="#a855f7" />
                  <stop offset="75%" stopColor="#6366f1" />
                  <stop offset="100%" stopColor="#f97316" />
                </linearGradient>
              </defs>
              <motion.path
                d="M 50 50 Q 80 100, 50 150 T 50 250 T 50 350 T 50 450"
                stroke="url(#roadmapGradient)"
                strokeWidth="6"
                strokeLinecap="round"
                fill="none"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 0.3 }}
                transition={{ duration: 2, ease: "easeInOut" }}
              />
            </svg>

            {/* Steps */}
            <div className="relative z-10 space-y-3">
              {[
                { emoji: '🧩', title: 'Temel Blok Kodlama', color: '#10b981', delay: 0 },
                { emoji: '⚙️', title: 'Algoritma Geliştirme', color: '#3b82f6', delay: 0.15 },
                { emoji: '💻', title: 'Metin Tabanlı Kodlama', color: '#a855f7', delay: 0.3 },
                { emoji: '🤖', title: 'Yapay Zeka Temelleri', color: '#6366f1', delay: 0.45 },
                { emoji: '🚀', title: 'Proje Geliştirme', color: '#f97316', delay: 0.6 },
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: step.delay, duration: 0.6, type: "spring" }}
                  className="flex items-center gap-3"
                  style={{ marginLeft: i % 2 === 0 ? '0' : '40px' }}
                >
                  {/* Node Circle */}
                  <motion.div
                    className="relative flex-shrink-0"
                    whileHover={{ scale: 1.2, rotate: 360 }}
                    transition={{ duration: 0.5 }}
                  >
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg"
                      style={{ 
                        backgroundColor: step.color,
                        boxShadow: `0 4px 20px ${step.color}40`
                      }}
                    >
                      {step.emoji}
                    </div>
                    {/* Connection dot */}
                    <motion.div
                      className="absolute -left-6 top-1/2 w-2 h-2 rounded-full"
                      style={{ backgroundColor: step.color, transform: 'translateY(-50%)' }}
                      animate={{ scale: [1, 1.5, 1] }}
                      transition={{ duration: 2, repeat: Infinity, delay: step.delay }}
                    />
                  </motion.div>

                  {/* Card */}
                  <motion.div
                    className="flex-1 bg-white/90 backdrop-blur-sm rounded-xl shadow-md p-2.5 border-l-4"
                    style={{ borderColor: step.color }}
                    whileHover={{ 
                      scale: 1.03, 
                      x: 5,
                      boxShadow: `0 8px 30px ${step.color}30`
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <h3 
                        className="font-bold text-sm text-gray-800"
                        style={{ color: step.color }}
                      >
                        {step.title}
                      </h3>
                      <motion.div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                        style={{ backgroundColor: step.color }}
                        animate={{ scale: [1, 1.15, 1] }}
                        transition={{ duration: 2, repeat: Infinity, delay: step.delay }}
                      >
                        {i + 1}
                      </motion.div>
                    </div>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 1,
      title: 'AI-14 Aila Chat',
      subtitle: 'Güvenli ve Etik Yapay Zeka',
      description: 'K-12 için özel geliştirilmiş, güvenli AI asistanı.',
      color: 'from-purple-500 via-pink-500 to-rose-500',
      icon: '🤖',
      visual: (
        <div className="relative w-full h-full flex items-center justify-center">
          {/* Chat interface mockup */}
          <div className="relative w-[90%] h-[90%] bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl shadow-2xl p-6 overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-3 mb-4 pb-3 border-b border-purple-200">
              <motion.div
                className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white text-xl"
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                🤖
              </motion.div>
              <div>
                <div className="font-bold text-gray-800">Aila Chat</div>
                <div className="text-xs text-gray-500">AI-14 Güvenli Asistan</div>
              </div>
            </div>

            {/* Messages */}
            <div className="space-y-3">
              {[
                { text: "Merhaba! Sana nasıl yardımcı olabilirim?", isAI: true, delay: 0 },
                { text: "Matematik ödevimde yardım ister misin?", isAI: false, delay: 0.3 },
                { text: "Tabii ki! Hangi konuda yardıma ihtiyacın var?", isAI: true, delay: 0.6 },
              ].map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: msg.delay }}
                  className={`flex ${msg.isAI ? 'justify-start' : 'justify-end'}`}
                >
                  <div
                    className={`max-w-[70%] p-3 rounded-2xl ${
                      msg.isAI
                        ? 'bg-white text-gray-800 shadow-md'
                        : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                    }`}
                  >
                    <p className="text-sm">{msg.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Typing indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="flex gap-1 mt-4"
            >
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="w-2 h-2 bg-purple-400 rounded-full"
                  animate={{ y: [0, -5, 0] }}
                  transition={{
                    duration: 0.6,
                    repeat: Infinity,
                    delay: i * 0.1,
                  }}
                />
              ))}
            </motion.div>
          </div>
        </div>
      ),
    },
    {
      id: 2,
      title: 'Code AI Asistanı',
      subtitle: 'Yapay Zeka Destekli Kodlama',
      description: 'Kod yazarken yanında AI rehberin olsun.',
      color: 'from-green-500 via-emerald-500 to-teal-500',
      icon: '💻',
      visual: (
        <div className="relative w-full h-full flex items-center justify-center">
          {/* Code editor mockup */}
          <div className="relative w-[90%] h-[90%] bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl overflow-hidden">
            {/* Editor header */}
            <div className="bg-gray-800 px-4 py-2 flex items-center gap-2 border-b border-gray-700">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <div className="ml-2 text-xs text-gray-400">main.py</div>
            </div>

            {/* Code content */}
            <div className="p-4 font-mono text-sm space-y-2">
              {[
                { text: 'def calculate_area(radius):', color: 'text-purple-400', delay: 0 },
                { text: '    # AI Öneri: pi değerini kullan', color: 'text-gray-500', delay: 0.2 },
                { text: '    pi = 3.14159', color: 'text-blue-400', delay: 0.4 },
                { text: '    return pi * radius ** 2', color: 'text-green-400', delay: 0.6 },
              ].map((line, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: line.delay }}
                  className={line.color}
                >
                  {line.text}
                </motion.div>
              ))}

              {/* AI suggestion popup */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="mt-4 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-lg p-3"
              >
                <div className="flex items-start gap-2">
                  <div className="text-green-400 text-lg">✨</div>
                  <div>
                    <div className="text-green-400 text-xs font-bold mb-1">AI Öneri</div>
                    <div className="text-gray-300 text-xs">
                      math.pi kullanarak daha hassas sonuç alabilirsin!
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 3,
      title: 'Keşfet & Hub',
      subtitle: 'Merakını Takip Et',
      description: 'İlgi alanlarına göre özelleştirilmiş içerikler keşfet.',
      color: 'from-orange-500 via-amber-500 to-yellow-500',
      icon: '🔍',
      visual: (
        <div className="relative w-full h-full flex items-center justify-center p-4">
          {/* Split View - Keşfet & Hub */}
          <div className="w-full h-full grid grid-cols-2 gap-4">
            
            {/* Keşfet Section */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl shadow-xl overflow-hidden border-2 border-green-200/50"
            >
              <div className="bg-gradient-to-r from-green-500 to-teal-500 px-3 py-2 flex items-center gap-2">
                <span className="text-2xl">🔍</span>
                <div>
                  <h3 className="text-white font-bold text-sm">Keşfet</h3>
                  <p className="text-white/80 text-[10px]">İçerik Keşfi</p>
                </div>
              </div>
              
              <div className="p-3 space-y-2">
                {/* Search bar */}
                <div className="bg-white rounded-lg px-2 py-1.5 flex items-center gap-2 shadow-sm">
                  <span className="text-gray-400 text-xs">🔍</span>
                  <span className="text-gray-400 text-[10px]">İçerik ara...</span>
                </div>
                
                {/* Content cards */}
                {[
                  { emoji: '🎯', title: 'Ayın Teması', color: 'from-orange-400 to-red-400', delay: 0 },
                  { emoji: '🧠', title: 'Yapay Zeka', color: 'from-purple-400 to-pink-400', delay: 0.1 },
                  { emoji: '🎮', title: 'Oyunlar', color: 'from-blue-400 to-cyan-400', delay: 0.2 },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: item.delay }}
                    whileHover={{ scale: 1.03, x: 3 }}
                    className="bg-white rounded-lg p-2 shadow-sm border border-gray-100 cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center text-sm`}>
                        {item.emoji}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-[11px] text-gray-800">{item.title}</h4>
                        <div className="flex gap-1 mt-0.5">
                          <span className="text-[8px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-semibold">Kolay</span>
                          <span className="text-[8px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-semibold">Scratch</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Hub Section */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-xl overflow-hidden border-2 border-blue-200/50"
            >
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-3 py-2 flex items-center gap-2">
                <span className="text-2xl">⭐</span>
                <div>
                  <h3 className="text-white font-bold text-sm">Hub</h3>
                  <p className="text-white/80 text-[10px]">Topluluk Merkezi</p>
                </div>
              </div>
              
              <div className="p-3 space-y-2">
                {/* Categories */}
                <div className="flex gap-1">
                  {['🧩', '🤖', '🎮', '💻'].map((emoji, i) => (
                    <motion.button
                      key={i}
                      whileHover={{ scale: 1.1 }}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm ${
                        i === 0 
                          ? 'bg-gradient-to-br from-blue-500 to-purple-600 shadow-md' 
                          : 'bg-white border border-gray-200'
                      }`}
                    >
                      {emoji}
                    </motion.button>
                  ))}
                </div>
                
                {/* Project cards */}
                {[
                  { title: 'Codecizz Projesi', user: 'Ömer A.', likes: 5, color: 'from-green-400 to-emerald-400', delay: 0 },
                  { title: 'Hedef Projesi', user: 'Ayşe K.', likes: 8, color: 'from-blue-400 to-cyan-400', delay: 0.1 },
                  { title: 'Araba Çiz', user: 'Mehmet D.', likes: 3, color: 'from-purple-400 to-pink-400', delay: 0.2 },
                ].map((project, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: project.delay + 0.2 }}
                    whileHover={{ scale: 1.03, x: 3 }}
                    className="bg-white rounded-lg p-2 shadow-sm border border-gray-100 cursor-pointer"
                  >
                    <div className="flex gap-2">
                      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${project.color} flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0`}>
                        IMG
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-[11px] text-gray-800 truncate">{project.title}</h4>
                        <p className="text-[9px] text-gray-500 truncate">{project.user}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[9px] flex items-center gap-0.5">❤️ {project.likes}</span>
                          <span className="text-[9px] flex items-center gap-0.5">💬 {Math.floor(Math.random() * 5)}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Connecting animation */}
          <motion.div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-green-400 via-blue-400 to-purple-500 opacity-20 blur-xl"></div>
          </motion.div>
        </div>
      ),
    },
  ];

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setActiveTab((prev) => (prev + 1) % features.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, features.length]);

  const handleTabClick = (index) => {
    setActiveTab(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 10 seconds
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden bg-gradient-to-br from-gray-50 via-white to-purple-50/30">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width: Math.random() * 400 + 100,
              height: Math.random() * 400 + 100,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: `radial-gradient(circle, ${
                ['rgba(108, 93, 211, 0.03)', 'rgba(236, 72, 153, 0.03)', 'rgba(59, 130, 246, 0.03)'][i % 3]
              } 0%, transparent 70%)`,
            }}
            animate={{
              x: [0, Math.random() * 100 - 50],
              y: [0, Math.random() * 100 - 50],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: Math.random() * 20 + 20,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Header */}
      <div className="relative z-30">
        <Header logo={talent14Logo} />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center relative z-10 px-3 sm:px-4 md:px-8 lg:px-12 pt-20 sm:pt-24 md:pt-28 pb-12 sm:pb-16 md:pb-20">
        {/* Main Title - Top Center */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-3 sm:mb-4 md:mb-6 w-full"
        >
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-3 sm:mb-4 px-2">
            <span className="text-gray-900">K-12'de </span>
            <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 bg-clip-text text-transparent">
              Güvenli Yapay Zeka Öncüsü
            </span>
          </h1>
          
        </motion.div>

        <div className="max-w-7xl w-full mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 items-center">
            
            {/* Left Side - Selection Area */}
            <div className="space-y-2 sm:space-y-3">

              {/* Feature Tabs */}
              <div className="space-y-1 sm:space-y-1.5">
                {features.map((feature, index) => (
                  <motion.div
                    key={feature.id}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 + index * 0.1 }}
                    onClick={() => handleTabClick(index)}
                    className={`relative cursor-pointer rounded-lg p-2 sm:p-3 transition-all duration-500 ${
                      activeTab === index
                        ? 'bg-white shadow-2xl border-2 border-purple-200'
                        : 'bg-white/50 hover:bg-white/80 border-2 border-transparent hover:border-gray-200'
                    }`}
                    whileHover={{ scale: 1.02, x: 5 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {/* Active indicator */}
                    <AnimatePresence>
                      {activeTab === index && (
                        <motion.div
                          initial={{ scaleY: 0 }}
                          animate={{ scaleY: 1 }}
                          exit={{ scaleY: 0 }}
                          className={`absolute left-0 top-0 w-1 sm:w-1.5 h-full bg-gradient-to-b ${feature.color} rounded-l-lg`}
                        />
                      )}
                    </AnimatePresence>

                    <div className="flex items-start gap-2 sm:gap-2.5">
                      {/* Icon */}
                      <motion.div
                        className={`text-xl sm:text-2xl transition-transform duration-300 ${
                          activeTab === index ? 'scale-110' : 'scale-100'
                        }`}
                        animate={activeTab === index ? { rotate: [0, -10, 10, -10, 0] } : {}}
                        transition={{ duration: 0.5 }}
                      >
                        {feature.icon}
                      </motion.div>

                      {/* Content */}
                      <div className="flex-1">
                        <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 mb-0">
                          {feature.title}
                        </h3>
                        <p className={`text-[9px] sm:text-[10px] md:text-xs font-semibold bg-gradient-to-r ${feature.color} bg-clip-text text-transparent mb-0.5`}>
                          {feature.subtitle}
                        </p>
                        <AnimatePresence mode="wait">
                          {activeTab === index && (
                            <motion.p
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="text-gray-600 text-[9px] sm:text-[10px] md:text-xs"
                            >
                              {feature.description}
                            </motion.p>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Arrow indicator */}
                      <motion.div
                        animate={{ x: activeTab === index ? [0, 5, 0] : 0 }}
                        transition={{ duration: 1, repeat: activeTab === index ? Infinity : 0 }}
                        className={`text-base sm:text-lg transition-opacity ${
                          activeTab === index ? 'opacity-100' : 'opacity-0'
                        }`}
                      >
                        →
                      </motion.div>
                    </div>

                    {/* Progress bar for auto-play */}
                    {activeTab === index && isAutoPlaying && (
                      <motion.div
                        className={`absolute bottom-0 left-0 h-0.5 sm:h-1 bg-gradient-to-r ${feature.color} rounded-full`}
                        initial={{ width: '0%' }}
                        animate={{ width: '100%' }}
                        transition={{ duration: 5, ease: "linear" }}
                      />
                    )}
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Right Side - Visual Display */}
            <motion.div
              className="relative h-[300px] sm:h-[350px] md:h-[400px] lg:h-[500px]"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, x: 50, rotateY: -15 }}
                  animate={{ opacity: 1, x: 0, rotateY: 0 }}
                  exit={{ opacity: 0, x: -50, rotateY: 15 }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                  className="absolute inset-0"
                >
                  {/* Glow effect */}
                  <div
                    className={`absolute inset-0 blur-3xl opacity-20 bg-gradient-to-r ${features[activeTab].color}`}
                  />
                  
                  {/* Visual content */}
                  <div className="relative w-full h-full">
                    {features[activeTab].visual}
                  </div>
                </motion.div>
              </AnimatePresence>
            </motion.div>

          </div>

          {/* CTA Button - Centered Below */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="text-center mt-4 sm:mt-6 md:mt-8"
          >
            <motion.a
              href="https://app.talent14.com/"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(108, 93, 211, 0.4)" }}
              whileTap={{ scale: 0.95 }}
              className="inline-block px-5 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-primary to-purple-600 text-white rounded-xl font-bold text-sm sm:text-base md:text-lg shadow-glow-xl relative overflow-hidden group"
            >
              <span className="relative z-10">Hemen Başla</span>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"
                animate={{
                  x: ['-100%', '100%'],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
            </motion.a>
          </motion.div>
        </div>
      </div>

    </section>
  );
};

export default HeroSection;
